import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from './FormComponents/MultiSelect';
import { X, ChevronRight, ChevronLeft, Briefcase, Building2, MapPin, Mail } from 'lucide-react';

import { supabase } from '../../../lib/supabase';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { LocationPicker } from '../../LocationPicker';

interface JobFormProps {
  onClose: () => void;
  onSuccess: () => void;
  editId?: string;
}

type Step = 'basics' | 'details' | 'requirements' | 'contact';

interface StepConfig {
  id: Step;
  title: string;
  icon: React.ComponentType<any>;
}

const steps: StepConfig[] = [
  { id: 'basics', title: 'Basic Info', icon: Briefcase },
  { id: 'details', title: 'Details', icon: Building2 },
  { id: 'requirements', title: 'Requirements', icon: MapPin },
  { id: 'contact', title: 'Contact', icon: Mail },
];

const JOB_TYPES = [
  { value: 'full_time', labelKey: 'fullTime' },
  { value: 'part_time', labelKey: 'partTime' },
  { value: 'contract', labelKey: 'contract' },
  { value: 'freelance', labelKey: 'freelance' },
  { value: 'internship', labelKey: 'internship' },
  { value: 'volunteer', labelKey: 'volunteer' },
];

const COMMON_REQUIREMENTS = [
  { value: 'certification', labelKey: 'professionalCert' },
  { value: 'experience', labelKey: 'previousExperience' },
  { value: 'education', labelKey: 'relevantEducation' },
  { value: 'language', labelKey: 'languageSkills' },
  { value: 'remote', labelKey: 'remoteWorkExp' },
];

export function JobForm({ onClose, onSuccess, editId }: JobFormProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<Step>('basics');
  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    description: '',
    location: '',
    country: '',
    job_type: '',
    salary_range: '',
    requirements: [] as string[],
    contact_email: '',
    latitude: null as number | null,
    longitude: null as number | null
  });
  const [loading, setLoading] = useState(!!editId);
  const [error, setError] = useState('');

  useEffect(() => {
    // Fetch job data if editing
    if (editId) {
      const fetchJob = async () => {
        setLoading(true);
        try {
          const { data, error } = await supabase
            .from('job_offers')
            .select('*')
            .eq('id', editId)
            .single();

          if (error) throw error;
          if (!data) throw new Error('Job not found');

          setFormData({
            title: data.title,
            company_name: data.company_name,
            description: data.description,
            location: data.location,
            country: data.country || '',
            job_type: data.job_type,
            salary_range: data.salary_range,
            requirements: data.requirements || [],
            contact_email: data.contact_email,
            latitude: (data as any).latitude || null,
            longitude: (data as any).longitude || null
          });
        } catch (err: any) {
          console.error('Error fetching job:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchJob();
    }
  }, [editId]);

  const isStepValid = () => {
    switch (currentStep) {
      case 'basics':
        return formData.title && formData.company_name && formData.description;
      case 'details':
        return formData.location && formData.country && formData.job_type && formData.latitude !== null && formData.longitude !== null;
      case 'requirements':
        return formData.requirements.length > 0 && formData.salary_range;
      case 'contact':
        return formData.contact_email;
    }
  };

  const handleNext = () => {
    if (!isStepValid()) {
      setError(t('forms.common.fillRequiredFields'));
      return;
    }
    
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
    } else {
      handleSubmit();
    }
    setError('');
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
    setError('');
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    const jobData = {
      user_id: (await supabase.auth.getUser()).data.user?.id,
      title: formData.title,
      company_name: formData.company_name,
      description: formData.description,
      location: formData.location,
      country: formData.country,
      job_type: formData.job_type,
      salary_range: formData.salary_range,
      requirements: formData.requirements,
      contact_email: formData.contact_email,
      latitude: formData.latitude,
      longitude: formData.longitude
    };

    try {
      let error;
      
      if (editId) {
        // Update existing job
        const { error: updateError } = await supabase
          .from('job_offers')
          .update(jobData)
          .eq('id', editId);
        
        error = updateError;
      } else {
        // Create new job
        const { error: insertError } = await supabase
          .from('job_offers')
          .insert([jobData]);
        
        error = insertError;
      }

      if (error) throw error;
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">{t('forms.job.jobTitle')}</Label>
              <Input
                id="title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('forms.job.titlePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">{t('forms.job.companyName')}</Label>
              <Input
                id="company_name"
                type="text"
                required
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder={t('forms.job.companyPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('forms.job.jobDescription')}</Label>
              <Textarea
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('forms.job.descriptionPlaceholder')}
                rows={4}
              />
            </div>
          </div>
        );

      case 'details':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="job_type">{t('forms.job.jobType')}</Label>
              <Select
                value={formData.job_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, job_type: value }))}
              >
                <SelectTrigger id="job_type">
                  <SelectValue placeholder={t('forms.job.selectJobType')} />
                </SelectTrigger>
                <SelectContent>
                  {JOB_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {t(`forms.job.${type.labelKey}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <LocationPicker
              latitude={formData.latitude}
              longitude={formData.longitude}
              onChange={(lat, lng, address, countryCode) => setFormData(prev => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                location: address || prev.location,
                country: countryCode || prev.country
              }))}
              label={t('forms.job.jobLocation')}
              helperText={t('forms.job.searchLocation')}
            />
          </div>
        );

      case 'requirements':
        return (
          <div className="space-y-6">
            <MultiSelect
              label={t('forms.job.requirements')}
              options={COMMON_REQUIREMENTS.map(r => ({ value: r.value, label: t(`forms.job.${r.labelKey}`) }))}
              selectedValues={formData.requirements}
              onChange={(values) => setFormData(prev => ({ ...prev, requirements: values }))}
              required
            />

            <div className="space-y-2">
              <Label htmlFor="salary_range">{t('forms.job.salaryRange')}</Label>
              <Input
                id="salary_range"
                type="text"
                required
                value={formData.salary_range}
                onChange={(e) => setFormData(prev => ({ ...prev, salary_range: e.target.value }))}
                placeholder={t('forms.job.salaryPlaceholder')}
              />
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="contact_email">{t('forms.job.contactEmail')}</Label>
              <Input
                id="contact_email"
                type="email"
                required
                value={formData.contact_email}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
              />
            </div>
          </div>
        );
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'basics': return t('forms.job.basicInformation');
      case 'details': return t('forms.job.jobDetails');
      case 'requirements': return t('forms.job.requirementsSalary');
      case 'contact': return t('forms.job.contactInformation');
      default: return '';
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  const modalOverlayClass = "fixed inset-0 min-h-[100dvh] bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-0 lg:p-4";
  const modalInnerClass = "bg-background rounded-none lg:rounded-lg w-full h-full min-h-[100dvh] lg:min-h-0 lg:h-auto lg:max-w-2xl lg:max-h-[90vh] overflow-hidden flex flex-col lg:shadow-2xl";

  if (loading) {
    const loadingContent = (
      <div className={modalOverlayClass}>
        <div className={modalInnerClass}>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
              <span className="ml-3 text-content">{t('forms.job.loadingData')}</span>
            </div>
          </div>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(loadingContent, document.body) : loadingContent;
  }

  const content = (
    <div className={modalOverlayClass}>
      <div className={modalInnerClass}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-content">
              {getStepTitle()}
            </h2>
            <p className="text-sm text-content/60 mt-1">{t('forms.common.stepOf', { current: currentStepIndex + 1, total: steps.length })}</p>
          </div>
          <button onClick={onClose} className="text-content/60 hover:text-content">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  index <= currentStepIndex ? 'bg-accent-text' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">{error}</div>
          )}

          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-between items-center">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 'basics'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 'basics'
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-content hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {t('forms.common.previous')}
          </button>

          {currentStep !== 'contact' ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                isStepValid()
                  ? 'bg-accent-text hover:bg-accent-text/90'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {t('forms.common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || !isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                loading || !isStepValid()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-accent-text hover:bg-accent-text/90'
              }`}
            >
              {loading ? (editId ? t('forms.common.updating') : t('forms.common.creating')) : (editId ? t('forms.job.updateJob') : t('forms.job.createJob'))}
            </button>
          )}
        </div>
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}